import { DomainError } from './errors.js';

/** Gemi yerel tarihi, `YYYY-MM-DD` biçiminde. Saat dilimi taşımaz. */
export type LocalDate = string;

export interface DateParts {
  year: number;
  month: number;
  day: number;
}

const LOCAL_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const DAY_MS = 86_400_000;

/** `Date.UTC` 0–99 yıllarını 1900'lere çevirir; `setUTCFullYear` çevirmez. */
function utcMs(year: number, monthIndex: number, day: number): number {
  const date = new Date(0);
  date.setUTCFullYear(year, monthIndex, day);
  return date.getTime();
}

function daysInMonth(year: number, month: number): number {
  return new Date(utcMs(year, month, 0)).getUTCDate();
}

function format({ year, month, day }: DateParts): LocalDate {
  const y = String(year).padStart(4, '0');
  const m = String(month).padStart(2, '0');
  const d = String(day).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function parseLocalDate(value: string): DateParts {
  const match = LOCAL_DATE.exec(value);
  if (!match) {
    throw new DomainError('INVALID_LOCAL_DATE', `Geçersiz tarih: ${value}`);
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12 || day < 1 || day > daysInMonth(year, month)) {
    throw new DomainError('INVALID_LOCAL_DATE', `Geçersiz tarih: ${value}`);
  }
  return { year, month, day };
}

function toUtcMs(value: LocalDate): number {
  const { year, month, day } = parseLocalDate(value);
  return utcMs(year, month - 1, day);
}

function fromUtcMs(ms: number): LocalDate {
  const date = new Date(ms);
  return format({
    year: date.getUTCFullYear(),
    month: date.getUTCMonth() + 1,
    day: date.getUTCDate(),
  });
}

export function addDays(value: LocalDate, days: number): LocalDate {
  if (!Number.isInteger(days)) {
    throw new DomainError('INVALID_DAY_COUNT');
  }
  return fromUtcMs(toUtcMs(value) + days * DAY_MS);
}

/** Ay ekler; gün hedef ayda yoksa ayın son günü alınır (31 Ocak + 1 ay = 28/29 Şubat). */
export function addMonths(value: LocalDate, months: number): LocalDate {
  if (!Number.isInteger(months)) {
    throw new DomainError('INVALID_MONTH_COUNT');
  }
  const { year, month, day } = parseLocalDate(value);
  const index = month - 1 + months;
  const newYear = year + Math.floor(index / 12);
  const newMonth = (((index % 12) + 12) % 12) + 1;
  return format({
    year: newYear,
    month: newMonth,
    day: Math.min(day, daysInMonth(newYear, newMonth)),
  });
}

export function compareLocalDate(a: LocalDate, b: LocalDate): number {
  return Math.sign(toUtcMs(a) - toUtcMs(b));
}

/** `to - from`, gün olarak. */
export function daysBetween(from: LocalDate, to: LocalDate): number {
  return Math.round((toUtcMs(to) - toUtcMs(from)) / DAY_MS);
}
